import { Component, Input, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslocoModule } from '@jsverse/transloco';
import { ControlService } from '../library/control.service';
import { CallService } from 'src/app/services/learner/call.service';
import { AppConstants } from 'src/app/app-constants';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { AmplitudeTrackingEventPropValues } from 'src/app/utils/enums/amplitude/amplitudeTrackingEventPropValues.enum';
import { HotjarService } from 'src/app/services/external-tools/hotjar.service';
import { CallStoreService } from '../library/call-store/call-store.service';
import { TranslocoService } from '@jsverse/transloco';
import { LoggerService } from 'src/app/services/logger/logger.service';
import { QaiButtonComponent, QaiLoadingComponent } from '@qai/ui';
import { ButtonComponent } from 'src/app/components/shared/button/button.component';
import { ResultsGuidedService } from 'src/app/services/learner/results-guided-service';
import { ResultsConversationalService } from 'src/app/services/learner/results-conversational.service';

@Component({
  selector: 'app-post-call-hidden-results',
  templateUrl: './post-call-hidden-results.component.html',
  styleUrls: ['./post-call-hidden-results.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    TranslocoModule,
    QaiButtonComponent,
    ButtonComponent,
    QaiLoadingComponent,
  ],
})
export class PostCallHiddenResultsComponent implements OnDestroy {
  @Input() controlService: ControlService | undefined;
  callGuided: string = AppConstants.CALL_TYPE_GUIDED;
  callConversational: string = AppConstants.CALL_TYPE_CONVERSATIONAL;
  callType: string | undefined = '';
  practiceCallUuid: string = '';
  feedbackSubmitted: boolean = false;
  selectedFeedback: boolean | null = null;
  isRealtime: boolean = false;
  isCompleted: boolean = false;
  isLoading = signal<boolean>(true);
  isScoringComplete = signal<boolean>(false);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  helpEmail: string = 'support@quantified.ai';
  isSavedConversation = signal<boolean>(false);
  private pollingInterval: NodeJS.Timeout | null = null;
  private startTime: number = 0;
  private readonly pollingIntervalMs: number = 5000;
  private readonly timeoutDurationMs: number = 90000;
  private isFetchingResults = signal<boolean>(false);
  isPersonalizedCall: boolean = false;

  // Text display
  title: string = '';
  titleNormal: string = this.translocoService.translate(
    'COMPLETED_CONVERSATION_MESSAGE',
    {},
    'call'
  );
  titleInactivity: string = this.translocoService.translate(
    'CALL_ENDED_INACTIVITY',
    {},
    'call'
  );

  subtitle: string = '';
  subtitleNormal: string = this.translocoService.translate(
    'CONVERSATION_COMPLETION_FEEDBACK',
    {},
    'call'
  );
  subtitleNotMeet: string = this.translocoService.translate(
    'INTERACTION_THRESHOLD_NOT_MET',
    {},
    'call'
  );
  subtitleSavedConversation: string = this.translocoService.translate(
    'SAVED_CONVERSATION_MESSAGE',
    {},
    'call'
  );

  question: string = '';
  questionConversational: string = this.translocoService.translate(
    'REALISTIC_CONVERSATION_QUESTION',
    {},
    'call'
  );
  questionGuided: string = this.translocoService.translate(
    'KNOWLEDGEABLE_PREPARED_QUESTION',
    {},
    'call'
  );

  thankYouMessage: string = '';
  thankYouMessagePositive: string = this.translocoService.translate(
    'FEEDBACK_THANK_YOU',
    {},
    'call'
  );
  thankYouMessageNegative: string = this.translocoService.translate(
    'FEEDBACK_IMPROVEMENT_NOTE',
    {},
    'call'
  );

  constructor(
    private callService: CallService,
    private amplitudeService: AmplitudeService,
    private hotjarService: HotjarService,
    private callStore: CallStoreService,
    private translocoService: TranslocoService,
    private logger: LoggerService,
    private resultsGuidedService: ResultsGuidedService,
    private resultsConversationalService: ResultsConversationalService
  ) {}

  ngOnInit() {
    this.isRealtime = !!this.controlService?.isRealTimeCall;
    this.isCompleted = !!this.callStore.getIsCallComplete();
    this.isPersonalizedCall = this.callStore.getIsPersonalizedCall();
    console.log(
      `POST CALL HIDDEN RESULTS ::: Is call completed: ${this.isCompleted}`
    );
    const userInactive = !!this.controlService?.userInactivity;
    this.callType = this.controlService?.callType;

    this.title =
      userInactive && !this.isCompleted
        ? this.titleInactivity
        : this.titleNormal;

    // set the subtitle based on the call type
    this.subtitle = !this.isRealtime
      ? this.subtitleNormal
      : this.isCompleted
      ? this.subtitleNormal
      : this.subtitleNotMeet;

    this.errorMessage.set(
      this.translocoService.translate('ERROR_MESSAGE', {}, 'call')
    );

    this.helpEmail = 'support@quantified.ai';
    // set the question based on the call type
    if (this.callType === this.callGuided) {
      this.question = this.questionGuided;
    } else if (this.callType === this.callConversational) {
      this.question = this.questionConversational;
    }

    this.practiceCallUuid = this.controlService?.practiceCallUuid || '';
    this.selectedFeedback = null;

    this.logger.log('[CALL] end call screen (hidden results) - init', {
      isCompleted: this.isCompleted,
      userInactivity: userInactive,
      title: this.title,
      subtitle: this.subtitle,
      question: this.question,
    });

    // Sending the Event of Landing on End Call Page to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_PAGEVIEW,
      {
        call_type: this.controlService?.callType,
        call_uuid: this.controlService?.callUuid,
        practice_call_uuid: this.practiceCallUuid,
        is_personalized_call: this.isPersonalizedCall,
      }
    );

    if (this.isCompleted) {
      this.startPollingForScoring();
    } else {
      this.isLoading.set(false);
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private startPollingForScoring(): void {
    if (!this.practiceCallUuid) {
      this.logger.error(
        '[CALL] post-call-hidden-results - No practiceCallUuid, cannot start polling'
      );
      this.handleScoringError('No practice call UUID available');
      return;
    }

    this.isLoading.set(true);
    this.startTime = Date.now();
    this.checkScoringStatus();

    this.pollingInterval = setInterval(() => {
      const elapsedTime = Date.now() - this.startTime;

      if (elapsedTime >= this.timeoutDurationMs) {
        this.logger.log(
          '[CALL] post-call-hidden-results - Scoring timeout reached'
        );
        this.handleScoringTimeout();
        return;
      }

      if (
        !this.isScoringComplete() &&
        !this.isFetchingResults() &&
        !this.hasError()
      ) {
        this.checkScoringStatus();
      }
    }, this.pollingIntervalMs);
  }

  private checkScoringStatus(): void {
    if (
      this.isFetchingResults() ||
      this.isScoringComplete() ||
      this.hasError()
    ) {
      return;
    }

    this.isFetchingResults.set(true);

    let scoringCheck$;

    if (this.callType === this.callConversational) {
      scoringCheck$ =
        this.resultsConversationalService.getResultsConversationalData(
          this.practiceCallUuid
        );
    } else if (this.callType === this.callGuided) {
      scoringCheck$ =
        this.resultsGuidedService.getResultsGuidedData(
          this.practiceCallUuid
        );
    } else {
      scoringCheck$ =
        this.resultsGuidedService.getResultsGuidedData(
          this.practiceCallUuid
        );
    }

    scoringCheck$.subscribe({
      next: (res: any) => {
        this.isFetchingResults.set(false);

        if (res && res.practiceCall && res.practiceCall.score !== undefined) {
          this.handleScoringSuccess();
        }
      },
      error: (error: any) => {
        this.isFetchingResults.set(false);

        if (error.status === 500) {
          this.logger.log(
            '[CALL] post-call-hidden-results - Scoring still processing (500 error)'
          );
          return;
        }

        this.logger.error(
          '[CALL] post-call-hidden-results - Error checking scoring status',
          error
        );
        this.handleScoringError('Failed to check scoring status');
      },
    });
  }

  private handleScoringSuccess(): void {
    this.logger.log(
      '[CALL] post-call-hidden-results - Scoring completed successfully'
    );
    this.stopPolling();
    this.isScoringComplete.set(true);
    this.isLoading.set(false);
    this.isSavedConversation.set(true);
  }

  private handleScoringTimeout(): void {
    this.logger.log('[CALL] post-call-hidden-results - Scoring timeout');
    this.stopPolling();
    this.isLoading.set(false);
    this.hasError.set(true);
    this.errorMessage.set(
      this.translocoService.translate('ERROR_MESSAGE', {}, 'call')
    );
  }

  private handleScoringError(message: string): void {
    this.logger.error('[CALL] post-call-hidden-results - Scoring error', {
      message,
    });
    this.stopPolling();
    this.isLoading.set(false);
    this.hasError.set(true);
    this.errorMessage.set(
      this.translocoService.translate('ERROR_MESSAGE', {}, 'call')
    );
  }

  private stopPolling(): void {
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  submitFeedback(feedback: boolean): void {
    this.thankYouMessage = feedback
      ? this.thankYouMessagePositive
      : this.thankYouMessageNegative;
    // Sending the Event of Clicking on End Call Feedback to Amplitude
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_CLICK_FEEDBACK,
      {
        thumbs_type: feedback
          ? AmplitudeTrackingEventPropValues.FEEDBACK_THUMBS_UP
          : AmplitudeTrackingEventPropValues.FEEDBACK_THUMBS_DOWN,
        call_type: this.controlService?.callType,
        call_completed: this.isCompleted,
      }
    );

    this.selectedFeedback = feedback;
    this.feedbackSubmitted = true;

    this.logger.log(
      '[CALL] end call screen (hidden results) - click feedback',
      {
        isPositive: feedback,
      }
    );

    this.callService.submitFeedback(this.practiceCallUuid, feedback).subscribe({
      next: () => {
        console.log(
          'Response received successfully on submitting feedback at the end of the call'
        );
      },
      error: (err) => {
        this.logger.error(
          '[CALL] end call screen (hidden results) - submit feedback error',
          err
        );
      },
    });

    // Open Hotjar feedback modal when user clicks on dislike
    if (!feedback) {
      const accountUuid = localStorage.getItem('current_account_uuid') || '-';
      const attributes = {
        accountUuid,
        callType: this.callType,
        practiceCallUuid: this.practiceCallUuid,
      };
      this.hotjarService.openSurveyByEvent(
        'practice_feedback_dislike',
        attributes
      );

      this.logger.log('[CALL] end call screen (hidden results) - open survey');
    }
  }

  clickErrorModalOK(): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_ERROR_MODAL_OK,
      {
        call_type: this.controlService?.callType,
        call_uuid: this.controlService?.callUuid,
        practice_call_uuid: this.practiceCallUuid,
      }
    );

    this.controlService?.sendRequestToEndCall();

    const isNetworkError = !this.navigatorOnline;
    const isEndCallError = !this.isCompleted;

    this.logger.log(
      '[CALL] end call screen (hidden results) - click error modal OK',
      {
        isNetworkError: isNetworkError,
        isEndCallError: isEndCallError,
      }
    );
  }

  clickErrorModalReturnAssignment(): void {
    this.amplitudeService.trackEvent(
      AmplitudeTrackingEvents.END_CALL_ERROR_MODAL_ASSIGNMENT,
      {
        call_type: this.controlService?.callType,
        call_uuid: this.controlService?.callUuid,
        practice_call_uuid: this.practiceCallUuid,
      }
    );

    this.controlService?.goBackToAssigment();

    const isNetworkError = !this.navigatorOnline;
    const isEndCallError = !this.isCompleted;

    this.logger.log(
      '[CALL] end call screen (hidden results) - click error modal return assignment',
      {
        isNetworkError: isNetworkError,
        isEndCallError: isEndCallError,
      }
    );
  }

  protected get navigatorOnline(): boolean {
    const navigator = window.navigator;
    const isOnline = navigator.onLine;
    return isOnline;
  }
}
